import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MailComposer } from '@Components/MailComposer';
import { useSetError } from '@src/redux/modals';
import { getCleanEmail } from '@src/utils/fetch-utils';
import { CreditMemoRow, CreditMemoStatus, CreditMemoStatusType } from './credit-memos-utils';
import {
  sendCreditMemoEmail,
  useCreditMemoMailInfo,
  useCreditMemoPdfData
} from './use-credit-memos';

interface CreditMemoModalProps {
  row: CreditMemoRow;
  show: boolean;
  onHide: () => void;
  patchCreditMemo: (row: CreditMemoRow, status: CreditMemoStatusType) => Promise<boolean>;
}

export const CreditMemoModal = ({
  row,
  show,
  onHide,
  patchCreditMemo
}: CreditMemoModalProps): ReactNode => {
  const setError = useSetError();
  const { data: mailInfo, error: mailInfoError } = useCreditMemoMailInfo(row.id, row.spotIDs);
  const { data: pdfData, error: pdfError } = useCreditMemoPdfData(row);
  const [sending, setSending] = useState(false);

  const [subject, setSubject] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCC] = useState<string[]>([]);
  const [readOnlyCC, setReadOnlyCC] = useState<string[]>([]);
  const [bodyText, setBodyText] = useState('');
  const [agency, setAgency] = useState('bpm');

  // Seed the editable form fields once the pre-filled mail info loads.
  useEffect(() => {
    if (!mailInfo) return;
    setTo(mailInfo.to);
    setCC(mailInfo.cc);
    setReadOnlyCC(mailInfo.readOnlyCC);
    setAgency(mailInfo.agency);
    setBodyText(mailInfo.bodyText);
    setSubject(mailInfo.subject);
  }, [mailInfo]);

  useEffect(() => {
    const loadError = mailInfoError ?? pdfError;
    if (loadError) {
      setError({ message: loadError.message, reportError: loadError });
    }
  }, [mailInfoError, pdfError, setError]);

  // The blob comes from a cached React Query result, so the object URL is created (and cleaned
  // up) here rather than inside the queryFn -- a URL minted there would have no owner to ever
  // revoke it, leaking a full PDF blob per memo previewed in the session.
  const [previewUrl, setPreviewUrl] = useState<string>();
  useEffect(() => {
    if (!pdfData) return () => {};
    const url = URL.createObjectURL(pdfData.contents);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfData]);

  const onSend = useCallback(async () => {
    setSending(true);
    const cleanTo = getCleanEmail(to);
    if (!cleanTo) {
      setError({ title: 'Invalid Email', message: `"${to}" is not a valid email address` });
      setSending(false);
      return;
    }
    const cleanCC: string[] = [];
    for (const email of cc) {
      if (!email) {
        continue;
      }
      const cleanEmail = getCleanEmail(email);
      if (!cleanEmail) {
        setError({ title: 'Invalid Email', message: `"${email}" is not a valid email address` });
        setSending(false);
        return;
      }
      cleanCC.push(cleanEmail);
    }
    const body = bodyText
      .replace(/\n/g, '<br />')
      .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>');
    if (pdfError) {
      setError({ message: `Failed to load the PDF: ${pdfError.message}`, reportError: pdfError });
      setSending(false);
      return;
    }
    if (!pdfData) {
      setError({ message: 'The PDF is still loading -- please wait and try again.' });
      setSending(false);
      return;
    }
    try {
      await sendCreditMemoEmail({
        to: cleanTo,
        cc: [...cleanCC, ...readOnlyCC],
        agency,
        subject,
        body,
        // The same bucket/key the preview pane resolved to, not a hardcoded path -- older
        // memos can live in a legacy bucket the current-bucket-only path would miss.
        attachment: pdfData.attachment
      });
      // Only close once the status update also succeeds -- patchCreditMemo already surfaces its
      // own error dialog on failure, so the modal staying open (with the email already sent) is
      // the user's cue that the status still needs a retry, instead of a false "all done" close.
      const patched = await patchCreditMemo(row, CreditMemoStatus.SENT);
      setSending(false);
      if (patched) {
        onHide();
      }
    } catch (e) {
      setError({ message: `Failed to send PDF. ${e.message}`, reportError: e });
      setSending(false);
    }
  }, [
    to,
    cc,
    readOnlyCC,
    agency,
    subject,
    bodyText,
    pdfData,
    pdfError,
    row,
    patchCreditMemo,
    onHide,
    setError
  ]);

  return (
    <Modal size='xl' dialogClassName='credit-memo-modal' show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Send Credit Memo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MailComposer
          loaded={Boolean(mailInfo)}
          to={to}
          onToChange={setTo}
          cc={cc}
          onCCChange={setCC}
          readOnlyCC={readOnlyCC}
          subject={subject}
          onSubjectChange={setSubject}
          bodyText={bodyText}
          onBodyTextChange={setBodyText}
          onCancel={onHide}
          onSend={onSend}
          sendLoading={sending}
          previewUrl={previewUrl}
        />
      </Modal.Body>
    </Modal>
  );
};
